/* Shabbat / Yom Tov store-closed guard. Externalized from theme.liquid for performance
   (was an inline <script> on every page). Behavior unchanged; init deferred to idle. */
(function () {
  const CONFIG = {
    cacheMinutes: 60,
    messages: {
      alert: "🕯️ Our store is currently closed for Shabbat or Yom Tov. We’ll reopen after it ends.",
      title: "🕯️ Shabbat / Yom Tov",
      body: "Our store is currently closed for Shabbat or Yom Tov.<br>We’ll be back after it ends."
    },
    fallbackLocation: {
      latitude: 40.7128,
      longitude: -74.0060,
      tzid: "America/New_York",
      name: "New York"
    }
  };

  let observer = null;

  function getCache(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const maxAge = CONFIG.cacheMinutes * 60 * 1000;
      if (Date.now() - parsed.ts > maxAge) return null;
      return parsed.data;
    } catch {
      return null;
    }
  }

  function setCache(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {}
  }

  function getBrowserTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || CONFIG.fallbackLocation.tzid;
    } catch {
      return CONFIG.fallbackLocation.tzid;
    }
  }

  function timezoneToSearchCandidates(tzid) {
    const cleaned = (tzid || "")
      .replace(/^Etc\//, "")
      .replace(/^US\//, "")
      .replace(/^Canada\//, "")
      .replace(/^Brazil\//, "")
      .trim();

    const parts = cleaned.split("/").filter(Boolean);

    if (!parts.length) {
      return [CONFIG.fallbackLocation.name];
    }

    const candidates = [];

    // Best candidate: last segment, usually city name
    const last = parts[parts.length - 1].replace(/_/g, " ");
    candidates.push(last);

    // Second candidate: join trailing segments for cases like Indiana/Indianapolis
    if (parts.length >= 2) {
      const joined = parts.slice(1).join(" ").replace(/_/g, " ");
      if (!candidates.includes(joined)) candidates.push(joined);
    }

    // Third candidate: full path without continent
    const full = parts.join(" ").replace(/_/g, " ");
    if (!candidates.includes(full)) candidates.push(full);

    return candidates;
  }

  async function geocodeTimezone(tzid) {
    const cacheKey = `tz-geocode-${tzid}`;
    const cached = getCache(cacheKey);
    if (cached) return cached;

    const candidates = timezoneToSearchCandidates(tzid);

    for (const name of candidates) {
      try {
        const url = new URL("https://geocoding-api.open-meteo.com/v1/search");
        url.searchParams.set("name", name);
        url.searchParams.set("count", "10");
        url.searchParams.set("language", "en");
        url.searchParams.set("format", "json");

        const res = await fetch(url.toString(), {
          headers: { Accept: "application/json" }
        });

        if (!res.ok) continue;

        const data = await res.json();
        const results = Array.isArray(data?.results) ? data.results : [];
        if (!results.length) continue;

        // Prefer exact timezone match
        let match = results.find(r => r.timezone === tzid);

        // Otherwise use first reasonable result
        if (!match) {
          match = results[0];
        }

        if (match?.latitude && match?.longitude) {
          const location = {
            latitude: Number(match.latitude),
            longitude: Number(match.longitude),
            tzid: tzid,
            name: [match.name, match.country].filter(Boolean).join(", ")
          };
          setCache(cacheKey, location);
          return location;
        }
      } catch (e) {}
    }

    return CONFIG.fallbackLocation;
  }

  function buildHebcalUrl(location) {
    const url = new URL("https://www.hebcal.com/zmanim");
    url.searchParams.set("cfg", "json");
    url.searchParams.set("im", "1");
    url.searchParams.set("latitude", String(location.latitude));
    url.searchParams.set("longitude", String(location.longitude));
    url.searchParams.set("tzid", location.tzid);
    return url.toString();
  }

  async function getMelachaStatus(location) {
    const cacheKey = `hebcal-status-${location.latitude}-${location.longitude}-${location.tzid}`;
    const cached = getCache(cacheKey);
    if (cached) return cached;

    const res = await fetch(buildHebcalUrl(location), {
      headers: { Accept: "application/json" }
    });

    if (!res.ok) throw new Error("Hebcal request failed");

    const data = await res.json();
    setCache(cacheKey, data);
    return data;
  }

  function blockAction(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation?.();
    alert(CONFIG.messages.alert);
    return false;
  }

  function disableElement(el) {
    if (!el || el.dataset.shabbatBlocked === "1") return;
    el.dataset.shabbatBlocked = "1";

    if (el.tagName === "FORM") {
      el.addEventListener("submit", blockAction, true);
    } else {
      el.addEventListener("click", blockAction, true);
      el.addEventListener("touchstart", blockAction, true);
      el.style.cursor = "not-allowed";
      el.style.opacity = "0.65";
    }
  }

  function applyShabbatMode() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(disableElement);

    document.querySelectorAll(`
      a[href*="/checkout"],
      button[name="checkout"],
      input[name="checkout"],
      .shopify-payment-button button,
      .shopify-payment-button__button,
      [data-shopify="payment-button"]
    `).forEach(disableElement);

    if (!document.getElementById("shabbat-overlay")) {
      const overlay = document.createElement("div");
      overlay.id = "shabbat-overlay";
      overlay.innerHTML = `
        <div style="
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.86);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 20px;
          z-index: 999999;">
          <div style="max-width: 680px;">
            <div style="font-size: 32px; margin-bottom: 12px;">${CONFIG.messages.title}</div>
            <div style="font-size: 18px; line-height: 1.6;">${CONFIG.messages.body}</div>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
    }

    if (!observer) {
      observer = new MutationObserver(() => {
        document.querySelectorAll('form[action*="/cart/add"]').forEach(disableElement);
        document.querySelectorAll(`
          a[href*="/checkout"],
          button[name="checkout"],
          input[name="checkout"],
          .shopify-payment-button button,
          .shopify-payment-button__button,
          [data-shopify="payment-button"]
        `).forEach(disableElement);
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  async function init() {
    try {
      const tzid = getBrowserTimezone();
      const location = await geocodeTimezone(tzid);
      const hebcal = await getMelachaStatus(location);
      const isBlocked = !!hebcal?.status?.isAssurBemlacha;

      if (isBlocked) {
        applyShabbatMode();
      }
    } catch (err) {
      console.error("Shabbat/Yom Tov blocking error:", err);
    }
  }

  // Deferred + idle so it never blocks first paint or interaction.
  if ("requestIdleCallback" in window) {
    requestIdleCallback(init, { timeout: 3000 });
  } else {
    setTimeout(init, 1200);
  }
})();
